<template>
  <Card variant="tertiary" size="medium" :shadow="true" id="rp-inquiry-card">
    <View direction="column" gap="lg">
      <Link :to="propertyLink" :decoration="false">
        <View direction="row" gap="md">
          <PropertyPhotoPreview :image-url="propertyInquiry.property.coverImage" size="sm" />

          <View direction="column" :grow="1" gap="xs">
            <View direction="row" justify="space-between" gap="md">
              <Text variant="label-medium">{{ propertyInquiry.property.displayName }}</Text>
              <Text variant="label-small" color="secondary">
                {{ useDateFormat(propertyInquiry.createdAt.toString(), 'MMM DD') }}
              </Text>
            </View>
            <View direction="row" gap="sm" align="center">
              <InquiryStatusLabel :small="true" :dot="false" :status="propertyInquiry.stage" />
              <div class="dot" v-if="propertyInquiry.source"></div>
              <Text variant="label-small" color="secondary" v-if="propertyInquiry.source">
                {{ propertyInquiry.source }}
              </Text>
            </View>
          </View>
        </View>
      </Link>

      <View direction="column" gap="sm">
        <div v-if="showWantsToApply" class="wants-to-apply" :title="`Requested to apply on ${askedOn}`">
          <span class="wants-to-apply-dot"></span>
          <Text variant="label-small">{{ wantsToApplyLabel }} · {{ askedAgo }}</Text>
        </div>

        <View gap="sm" wrap="wrap">
          <Button variant="outline" size="small" @click="emit('openConversationClicked')">
            <Icon name="message-chat-square" size="extra-small" />
            See conversation
          </Button>

          <Button
            v-if="canSendApplication"
            variant="primary"
            size="small"
            :loading="isSendingApplication"
            :data-manage-action="propertyId"
            @click="sendApplication"
          >
            Send application
          </Button>
        </View>
      </View>

      <SendApplicationModal
        :renter="renter"
        :property-inquiry="propertyInquiry"
        :attestations="attestations"
        ref="renterProfileSendApplicationModalRef"
      />

      <ProvideInvestorAddressModal @addressSaved="handleAddressSaved" ref="provideInvestorAddressModalRef" />
    </View>
  </Card>
</template>

<script setup lang="ts">
import { useDateFormat, useTimeAgo } from '@vueuse/core';
import { computed, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import { Button, Card, Icon, Link, Text, View, serverErrorCode, useAlertsStore } from '@tallo/design-system';
import {
  applicationsService,
  PropertyAttestationsDto,
  transUnionAddressRequiredErrorCode,
} from '@tallo/investor/applications';
import { propertyDashboardRoute } from '@tallo/investor/navigation';
import { InquiryStatusLabel, PropertyInquiryDto } from '@tallo/investor/property';
import { PropertyPhotoPreview } from '@tallo/property';
import { RenterDto } from '@tallo/renter';
import { useTimeAgoShortMessages } from '@tallo/utility';

import { useRenterProfileStore } from '../store/renter-profile.store';
import ProvideInvestorAddressModal from './provide-investor-address-modal/provide-investor-address-modal.vue';
import SendApplicationModal from './send-application-modal/send-application-modal.vue';

const props = defineProps<{ propertyInquiry: PropertyInquiryDto; renter: RenterDto }>();
const emit = defineEmits(['openConversationClicked']);

const route = useRoute();
const router = useRouter();
const renterProfileStore = useRenterProfileStore();
const alertsStore = useAlertsStore();

const isSendingApplication = ref(false);
const attestations = ref<PropertyAttestationsDto | null>(null);
const renterProfileSendApplicationModalRef = ref<InstanceType<typeof SendApplicationModal>>();
const provideInvestorAddressModalRef = ref<InstanceType<typeof ProvideInvestorAddressModal>>();
const propertyId = computed(() => props.propertyInquiry.property.id);
const propertyLink = computed(() => propertyDashboardRoute(propertyId.value));
const canSendApplication = computed(() => !renterProfileStore.isApplicationSent(propertyId.value));

const applicationRequestedAt = computed(() => props.propertyInquiry.applicationRequestedAt ?? '');
// An ask the owner ignored no longer waits on them, so the profile stops calling it out
const showWantsToApply = computed(
  () => !!applicationRequestedAt.value && !props.propertyInquiry.applicationRequestIgnored && canSendApplication.value,
);
// The reason itself is read in the wants-to-apply panel; here it only names the kind of ask
const wantsToApplyLabel = computed(() =>
  props.propertyInquiry.applicationWithoutTourReason ? 'Wants to apply without a tour' : 'Wants to apply',
);
const askedAgo = useTimeAgo(applicationRequestedAt, { messages: useTimeAgoShortMessages });
const askedOn = useDateFormat(applicationRequestedAt, 'MMM D');

async function sendApplication() {
  await fetchAttestationsAndShowModal();
}

// The company is set up at TransUnion once; only the person who does it is asked for an address
async function fetchAttestationsAndShowModal() {
  isSendingApplication.value = true;

  try {
    attestations.value = await applicationsService.getPropertyAttestations(propertyId.value);

    renterProfileSendApplicationModalRef.value?.modalRef?.show();
  } catch (error) {
    if (serverErrorCode(error) === transUnionAddressRequiredErrorCode) {
      provideInvestorAddressModalRef.value?.modalRef?.show();
    } else {
      alertsStore.catchError(error);
    }
  } finally {
    isSendingApplication.value = false;
  }
}

async function handleAddressSaved() {
  await fetchAttestationsAndShowModal();
}

// `?sendApplication=1` is the deep link the assistant hands owners when a send needs the
// attestations confirmed here: open the send step for the property in the URL on arrival, and
// drop the flag first so a refresh or back-navigation does not reopen it.
onMounted(async () => {
  if (
    route.query.sendApplication !== '1' ||
    route.params.propertyId !== propertyId.value ||
    !canSendApplication.value
  ) {
    return;
  }

  const query = { ...route.query };
  delete query.sendApplication;
  await router.replace({ query });
  await sendApplication();
});
</script>

<style scoped lang="scss">
.showing-request-actions-card {
  max-width: 100%;
}

.wants-to-apply {
  display: flex;
  align-items: center;
  align-self: start;
  gap: var(--t-spacing-xs);
  white-space: nowrap;
  color: var(--t-color-neutral-800);
}

.wants-to-apply-dot {
  flex-shrink: 0;
  width: 6px;
  height: 6px;
  border-radius: var(--t-border-radius-half-rounded);
  background-color: var(--t-color-orange-500);
}

.dot {
  background-color: var(--t-text-color-secondary);
  width: var(--t-spacing-xs);
  height: var(--t-spacing-xs);
  border-radius: var(--t-border-radius-half-rounded);
}
</style>
